/*
 *
 * Disclosure, not a warning.
 *
 * A catalog patch corrects data a team owns without their file changing, so the
 * least we owe them is a plain statement on the entity page: which fields we
 * are overriding, what their repository still says, and where the override is
 * written down. Overriding data a team owns without telling them is how you
 * lose the mandate to do it.
 *
 */

import {
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  makeStyles
} from '@material-ui/core'
import { Alert, AlertTitle } from '@material-ui/lab'
import { EntityRefLink, useEntity } from '@backstage/plugin-catalog-react'
import { Code } from '../typography'
import {
  CATALOG_PATCHES_DOCS_PATH,
  patchEntityRef,
  readPatchedFieldValue,
  readWinningProvenance
} from './catalogPatchAnnotations'

// The banner is rendered outside the grid that spaces the cards below it, so
// the gap has to come from here.
const useStyles = makeStyles(theme => ({
  notice: {
    marginBottom: theme.spacing(2)
  }
}))

export function EntityPatchedNotice() {
  const classes = useStyles()
  const { entity } = useEntity()
  const entries = readWinningProvenance(entity)

  // The switch that renders this already tested for provenance, but a
  // malformed annotation must render nothing rather than an empty banner.
  if (entries.length === 0) {
    return null
  }

  const fieldCount = entries.length === 1 ? 'One field' : `${entries.length} fields`

  return (
    <Alert severity="info" variant="outlined" className={classes.notice}>
      <AlertTitle>
        {fieldCount} on this entity {entries.length === 1 ? 'is' : 'are'} corrected by a
        catalog patch
      </AlertTitle>

      <Typography variant="body2" component="div">
        The portal serves the corrected values below. Your repository is unchanged, so the
        patch keeps applying until the file agrees with it — fix the file and the patch
        retires itself.{' '}
        <Link href={CATALOG_PATCHES_DOCS_PATH}>What is a catalog patch?</Link>
      </Typography>

      <Table size="small">
        <TableBody>
          {entries.map(entry => (
            <TableRow key={`${entry.patch}/${entry.field}`}>
              <TableCell>
                <Code>{entry.field}</Code>
              </TableCell>
              <TableCell>
                shown as{' '}
                <Code>{readPatchedFieldValue(entity, entry.field) ?? '(none)'}</Code>
                {', '}
                your file says <Code>{entry.from || '(nothing)'}</Code>
              </TableCell>
              <TableCell>
                <EntityRefLink
                  entityRef={patchEntityRef(entry.patch)}
                  title={entry.patch}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Alert>
  )
}
